import { createApi } from '@reduxjs/toolkit/query/react';

import { RootState } from '@store/index';

import { TAG_USER } from '../../config/tags';
import {
  CONTACTS_LOGOUT,
  CONTACTS_RESEND_CONFIRMATION_EMAIL,
  CONTACTS_USER,
  CONTACTS_VALIDATE_CGU,
} from '../../config/urls';

import { baseQuery } from './baseQuery';

import type { User } from '../types/user/user';

// Define a service using a base URL and expected endpoints
export const userApi = createApi({
  reducerPath: 'user',
  tagTypes: [TAG_USER],
  baseQuery,
  refetchOnFocus: true,
  endpoints: builder => ({
    getUser: builder.query<User, void>({
      query: () => ({
        url: CONTACTS_USER,
      }),
    }),
    logoutUser: builder.query<string, void>({
      query: () => ({
        url: CONTACTS_LOGOUT,
        responseHandler: response => response.text(),
      }),
    }),
    validateCGU: builder.mutation<User, void>({
      query: () => ({
        url: CONTACTS_VALIDATE_CGU,
        method: 'PATCH',
        responseHandler: response => response.json(),
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data: userWithValidatedCGU } = await queryFulfilled;
          dispatch(
            userApi.util.updateQueryData('getUser', undefined, draft => {
              Object.assign(draft, userWithValidatedCGU);
            })
          );
          // eslint-disable-next-line no-empty
        } catch {}
      },
      invalidatesTags: [TAG_USER],
    }),
    resendConfirmationEmail: builder.mutation<void, string>({
      query: email => ({
        url: `${CONTACTS_RESEND_CONFIRMATION_EMAIL}?email=${encodeURIComponent(email)}`,
        method: 'POST',
      }),
    }),
  }),
});

export const getUser = (state: RootState) => userApi.endpoints.getUser.select()(state).data;
export const isUserAuthenticatedWithAcceptedCGUs = (state: RootState) =>
  userApi.endpoints.getUser.select()(state).data?.cgu ?? false;

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetUserQuery,
  useLogoutUserQuery,
  useValidateCGUMutation,
  useResendConfirmationEmailMutation,
} = userApi;
